import { combineEpics, ofType } from "redux-observable";
import { concatMap, from, map, mergeMap, of, withLatestFrom, catchError } from "rxjs";
import { RootState } from "../../../store/ConfigureStore";
import { getCableLength, getCableVoltData, getClearanceData, getLoadCharts, getMotorEquipments, getMotorLeadList, getWellCableList, getWellEquipments } from "../../../services/calculations/calculation-output/cable.service";
import { resetCableFields, setCableAmpacity, setCableClearance, setCableConductorTempDesign, setCableMaxCondutorTemp, setCableSurfaceVoltage, setCableTotalLength, setCableTotalVoltageLoss, setCableVDrop, setCableVoltageLoss1000, setCableCalculationComplete, setLockPlateList, setMotorChart, setMotorLeadList, setMotorLockEquipmentList, setPumpMotorLeadChart, setSealMotorLeadChart, setShowEquipmentTable, setTubingCableChart, setWellCableList, setWellEquipmentList, ShowEquipmentCableTable, setSelectedCable } from "../../../store/calculation/calculation-output/cableSlice";
import { setHiddenCableLockPlate, setHiddenCableMotorLead } from "../../../store/calculation/calculation-input/TubingSlice";


const calculateCableLength = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "motor/setMotorCalculationComplete",
            "tubing/setHiddenCableSurfaceCabl",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.motor?.MotorCalculationComplete)),
        ),
        concatMap(([actionData, tab1Input, tab2Input, MotorCalculationComplete]) => {
            if (MotorCalculationComplete) {
                const pmpSetngMD = tab2Input?.pumpSetting;
                const pmpSetngMDUnit = tab2Input?.unitPumpSettingListVal;
                const surfCable = tab1Input?.hdnCable_SurfCabl;
                const unitSys = tab1Input?.unitSys;
                const postData = [pmpSetngMD, pmpSetngMDUnit, surfCable, unitSys];

                return from(getCableLength(postData)).pipe(
                    map(data => {
                        return setCableTotalLength(data)
                    }),
                )
            }
            return of({ type: "" });
        }),
    )
};

const calculateVoltageLoss = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "cable/setCableTotalLength",
            "tubing/setHiddenCableType",
            "motor/setMotorLoadInput",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
            state$.pipe(map((state: RootState) => state?.cable?.cableTotalLength)),
            state$.pipe(map((state: RootState) => state?.motor)),
            state$.pipe(map((state: RootState) => state?.motor?.SelectedMotor)),
        ),
        concatMap(([actionData, tab1Input, tab3Input, cableTotalLength, MotorInput, SelectedMotor]) => {

            let motorSelectedVolt = SelectedMotor?.voltage;
            let estCablLen = tab1Input?.hdnCable_EstCabLng || "";
            let tempEstCablLen = cableTotalLength
            let surfCabl = +tab1Input?.hdnCable_SurfCabl;
            let cableID = tab1Input?.hdnCable_CableType;
            let botmHole = tab3Input?.bottomHoleTemp;
            let botmHoleUnit = tab3Input?.unitBottomHoleTempListVal;
            let unitSys = tab1Input?.unitSys;
            let mtrID = tab1Input?.hiddenMotrID;
            let temphpReq = MotorInput?.motor_HPInput || 0;
            let opFreq = tab3Input?.operatngFreq;
            let motorLoad = MotorInput?.MotorInput?.motorLoad;
            let postInputData = [cableID, tempEstCablLen, surfCabl, unitSys, botmHole, botmHoleUnit, mtrID, temphpReq, opFreq, motorLoad];

            return from(getCableVoltData(postInputData)).pipe(
                mergeMap(data => {
                    return [
                        setCableSurfaceVoltage(data ? data[0] : ''),
                        setCableTotalVoltageLoss(data ? data[1] : ''),
                        setCableVoltageLoss1000(data ? data[2] : ''),
                        setCableMaxCondutorTemp(data ? data[3] : ''),
                        setCableConductorTempDesign(data ? data[4] : ''),
                        setCableAmpacity(data ? data[5] : ''),
                        setCableVDrop(data ? (data[1] / motorSelectedVolt * 100).toFixed(2).toString() : ''),
                    ]
                }),
            )
        }),
    )
};

const fetchwellCableList = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "tubing/setCatalogType",
            "motor/setMotorCalculationComplete",
            "tubing/setHiddenMotorId",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.motor?.MotorCalculationComplete)),
            state$.pipe(map((state: RootState) => state?.projectHeader?.isNavigating)),
        ),
        concatMap(([actionData, tab1Input, MotorCalculationComplete, isNavigating]) => {
            // Don't make API calls during navigation
            if (isNavigating) {
                return of({ type: "" });
            }
            
            if (MotorCalculationComplete && tab1Input?.hiddenMotrID && tab1Input?.catalogType !== undefined) {
                const MotorSers = tab1Input?.hiddenMotrID;
                const catalogFilter = +tab1Input?.catalogType;

                // Additional validation to prevent API calls with invalid data
                if (MotorSers && catalogFilter >= 0) {
                    return from(getWellCableList({ data: MotorSers, catalogFilter: catalogFilter })).pipe(
                        mergeMap(data => {
                            return [
                                setWellCableList(data?.cableSizeList || []),
                                setLockPlateList(data?.lockPlateList || {})
                            ]
                        }),
                        // Handle errors gracefully
                        catchError(error => {
                            console.error('Error in fetchwellCableList epic:', error);
                            return of(
                                setWellCableList([]),
                                setLockPlateList({})
                            );
                        })
                    )
                }
            }
            return of({ type: "" });
        }),
    )
};

const selectLockPlate = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("cable/setLockPlateList"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.cable?.lockPlateList)),
        ),
        map(([actionData, tab1Input, lockPlateList]) => {
            if ([undefined, null]?.includes(tab1Input?.hdnCable_LockPlate) && lockPlateList) {
                const selectedLoackPlate = Object.entries(lockPlateList)[0][0];
                return setHiddenCableLockPlate(selectedLoackPlate)
            }
            return setHiddenCableLockPlate(tab1Input?.hdnCable_LockPlate)

        })


    )
};

const fetchMotorLeadList = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "tubing/setCatalogType",
            "motor/setMotorCalculationComplete",
            "tubing/setHiddenMotorId",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.motor?.MotorCalculationComplete)),
        ),
        concatMap(([actionData, tab1Input, MotorCalculationComplete]) => {
            if (MotorCalculationComplete) {
                const MotorSers = tab1Input?.hiddenMotrID;
                const catalogFilter = +tab1Input?.catalogType;
                if (MotorSers) {
                    return from(getMotorLeadList({ data: MotorSers, catalogFilter: catalogFilter })).pipe(
                        mergeMap(data => {
                            return of(
                                setMotorLeadList(data?.MotrLeadList),
                                setHiddenCableMotorLead(Object.values(data?.MotrLeadList)[0] || "")
                            );
                        }),
                    )
                } else {
                    return of(setMotorLeadList([]))
                }
            }
            return of({ type: "" });
        }),
    )
};

const fetchWellEquipment = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "well/setSettingDepth",
            "cable/fetchCableWellEquipment",
            "motor/setMotorList"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.cable?.wellCableList)),
        ),
        concatMap(([actionData, tab1Input, tab2Input, wellCableList]) => {

            const foundCable = wellCableList.find((cable: any) => cable?.id === tab1Input?.hdnCable_CableType);
            const awgId = foundCable?.awgId ?? 0;
            const pumpSetting = tab2Input?.pumpSetting ?? 0;
            const catalogFilter = +tab1Input?.catalogType;
            if (pumpSetting) {
                return from(getWellEquipments({ awgId: awgId, pumpSetting: pumpSetting, catalogFilter: catalogFilter })).pipe(
                    mergeMap(data => {
                        if (data?.length > 0) {
                            return [
                                setShowEquipmentTable(true),
                                setWellEquipmentList(data)
                            ]
                        } else {
                            return of({ type: "" });
                        }
                    }),
                )
            }
            return of({ type: "" });
        }),
    )
};

const fetchMotorEquipment = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "cable/fetchCableMotorEquipment",
            "motor/setMotorList",
            "tubing/setCatalogType",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.motor)),
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
        ),
        concatMap(([actionData, Motor, tab1Input]) => {

            let selectedMotor = Motor?.MotorList?.find((x: any) => x.id === tab1Input?.hiddenMotrID) ?? [];
            let series = selectedMotor?.series
            const catalogFilter = tab1Input?.catalogType;

            if (![null, undefined, ""]?.includes(catalogFilter) && series) {
                return from(getMotorEquipments({ series: series, catalogFilter: catalogFilter })).pipe(
                    mergeMap(data => {
                        if (data?.length > 0) {
                            return [
                                ShowEquipmentCableTable(true),
                                setMotorLockEquipmentList(data)
                            ]
                        } else {
                            return of({ type: "" });
                        }
                    }),
                )
            }
            return of({ type: "" });
        }),
    )
};

const fetchClearanceDataLoad = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "tubing/setHiddenCableLockPlate",
            "tubing/setHiddenCableMotorLead",
            "tubing/setHiddenCableDriftId",
            "tubing/setHiddenCablePumpLockPlate",
            "tubing/setHiddenCableSealLockPlate",
            "tubing/setHiddenCableMotorLockPlate",
            "tubing/setHiddenCableType",
            "motor/setMotorCalculationComplete",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.cable?.lockPlateList)),
        ),
        concatMap(([actionData, tab1Input, lockPlateList]) => {
            var clearanceData: any = {}
            var selectedLockPlate = Object.entries(lockPlateList)?.find((x: any) => x?.[0] == tab1Input?.hdnCable_LockPlate)

            if (!selectedLockPlate) {
                selectedLockPlate = Object.entries(lockPlateList)?.find((x: any) => x?.[1] == tab1Input?.hdnCable_LockPlate)
            }

            if (selectedLockPlate) {
                const cablId = tab1Input?.hdnCable_CableType
                const casngOD = tab1Input?.casingOD?.toString()
                const casngWt = tab1Input?.casingWeight?.toString()
                const motrLead = tab1Input?.hdnCable_MotorLead
                if (casngOD == "" || casngWt == "") {
                    return of({ type: "" });
                }
                const pmpId = tab1Input?.hiddenPumpID
                const motrID = tab1Input?.hiddenMotrID
                let protctr1 = tab1Input?.hdnProtector_Protector1
                const protcrID = protctr1
                const unitSys = tab1Input?.unitSys
                const tubngOD = tab1Input?.tubingOD
                const tubngWt = tab1Input?.tubingWeight
                const pmpLck = selectedLockPlate?.[1] || 0
                const pmpLckFld = tab1Input?.hdnCable_PumpLockPlate
                const protrLckFld = tab1Input?.hdnCable_SealLockPlate
                const motrLckFld = tab1Input?.hdnCable_MotorLockPlate
                const drftIdFld = tab1Input?.hdnCable_IsDriftID

                const casngWtUnit = tab1Input?.unitCasingWeightListVal
                const tubngWtUnit = tab1Input?.unitTubingWeightListVal

                const postInputData = [cablId, casngOD, casngWt, pmpLck, motrID, motrLead, motrLckFld, protcrID, protrLckFld, pmpId, pmpLckFld, tubngOD, tubngWt, unitSys, drftIdFld, casngWtUnit, tubngWtUnit];

                return from(getClearanceData(postInputData)).pipe(
                    map(data => {
                        if (data && data?.length) {
                            for (let i = 0; i < data.length; i++) {
                                for (let k = 0; k < data[i].length; k++) {
                                    if (data[i][k] == "Tubing") {
                                        clearanceData.ClrnceForTubng = data[i][2]
                                        clearanceData.MsgsForTubng = data[i][0]
                                    }
                                    if (data[i][k] == "Motor") {
                                        clearanceData.ClrnceForMotr = data[i][2]
                                        clearanceData.MsgsForMotr = data[i][0]
                                    }
                                    if (data[i][k] == "Pump") {
                                        clearanceData.ClrnceForPmp = data[i][2]
                                        clearanceData.MsgsForPmp = data[i][0]
                                    }
                                    if (data[i][k] == "Protector") {
                                        clearanceData.ClrnceForPrtctr = data[i][2]
                                        clearanceData.MsgsForProtctr = data[i][0]
                                    }
                                }
                            }
                        }
                        return setCableClearance(clearanceData)
                    }),
                )
            }
            return of(setCableClearance(clearanceData))
        }),
    )
};

const loadClearanceChart = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "cable/setCableClearance",
            "cable/refreshChart",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.cable?.lockPlateList)),
        ),
        concatMap(([actionData, tab1Input, lockPlateList]) => {
            if (tab1Input !== null){
                var selectedLockPlate = Object.entries(lockPlateList)?.find((x: any) => x?.[0] == tab1Input?.hdnCable_LockPlate)

                if (!selectedLockPlate) {
                    selectedLockPlate = Object.entries(lockPlateList)?.find((x: any) => x?.[1] == tab1Input?.hdnCable_LockPlate)
                }

                const casingOD = tab1Input?.casingOD
                const casingWeight = tab1Input?.casingWeight
                const TubingOD = tab1Input?.tubingOD
                const TubingWeight = tab1Input?.tubingWeight
                const CableId = tab1Input?.hdnCable_CableType
                const PumpID = tab1Input?.hiddenPumpID
                const motorLeadID = tab1Input?.hdnCable_MotorLead
                const pumpLock = tab1Input?.hdnCable_PumpLockPlate
                const protectorID = tab1Input?.hdnProtector_Protector1
                const protectorlock = tab1Input?.hdnCable_SealLockPlate
                const motorid = tab1Input?.hiddenMotrID
                const motorlock = tab1Input?.hdnCable_MotorLockPlate
                const lockplateValue = selectedLockPlate?.[1] || 0
                const UnitSystem = tab1Input?.unitSys

                const casngWtUnit = tab1Input?.unitCasingWeightListVal
                const tubngWtUnit = tab1Input?.unitTubingWeightListVal

                const postInputSepData = [casingOD, casingWeight, TubingOD, TubingWeight, CableId, PumpID, motorLeadID, pumpLock, protectorID, protectorlock, motorid, motorlock, lockplateValue, UnitSystem, casngWtUnit, tubngWtUnit];

                const casngTubngVals = [casingOD, casingWeight, TubingOD, TubingWeight, CableId];
                let casngTubngFlg = false;

                for (let i = 0; i < casngTubngVals?.length; i++) {
                    if (casngTubngVals[i] == "") {
                        casngTubngFlg = false;
                        break;
                    }
                    else {
                        casngTubngFlg = true;
                    }
                }

                const casngPmpMtrVals = [casingOD, casingWeight, TubingOD, TubingWeight, PumpID, motorLeadID];
                let casngPmpMtrFlg = false;

                for (let i = 0; i < casngPmpMtrVals?.length; i++) {
                    if (casngPmpMtrVals[i] == "") {
                        casngPmpMtrFlg = false;
                        break;
                    }
                    else {
                        casngPmpMtrFlg = true;
                    }
                }

                const casngSealMtrVals = [casingOD, casingWeight, protectorID, motorLeadID];
                let casngSealMtrFlg = false;

                for (let i = 0; i < casngSealMtrVals?.length; i++) {
                    if (casngSealMtrVals[i] == "") {
                        casngSealMtrFlg = false;
                        break;
                    }
                    else {
                        casngSealMtrFlg = true;
                    }
                }

                const casngMtrVals = [casingOD, casingWeight, motorLeadID];
                let casngMtrFlg = false;

                for (let i = 0; i < casngMtrVals?.length; i++) {
                    if (casngMtrVals[i] == "") {
                        casngMtrFlg = false;
                        break;
                    }
                    else {
                        casngMtrFlg = true;
                    }
                }

                return from(getLoadCharts(postInputSepData)).pipe(
                    map(data => {
                        var TubingCableChart = []
                        var PumpMotorLeadChart = []
                        var SealMotorChart = []
                        var MotorChart = []
                        if (casngTubngFlg) {
                            TubingCableChart = data
                        }
                        if (casngPmpMtrFlg) {
                            PumpMotorLeadChart = data
                        }
                        if (casngSealMtrFlg) {
                            SealMotorChart = data
                        }
                        if (casngMtrFlg) {
                            MotorChart = data
                        }
                        return { TubingCableChart, PumpMotorLeadChart, SealMotorChart, MotorChart }
                    }),
                    mergeMap(data => {
                        return [
                            setTubingCableChart(data?.TubingCableChart),
                            setPumpMotorLeadChart(data?.PumpMotorLeadChart),
                            setSealMotorLeadChart(data?.SealMotorChart),
                            setMotorChart(data?.MotorChart),
                            setCableCalculationComplete(true),
                        ]
                    })
                )
            }
            return of({ type: "" });
        }),
    )
};


export const cableEpic = combineEpics(
    selectLockPlate,
    calculateCableLength,
    calculateVoltageLoss,
    fetchwellCableList,
    fetchMotorLeadList,
    fetchWellEquipment,
    fetchMotorEquipment,
    fetchClearanceDataLoad,
    loadClearanceChart,
);